/**
 * Extract Q&A pairs from a markdown article body's "Frequently Asked Questions"
 * section. Used to emit FAQPage JSON-LD for Google rich results.
 *
 * Convention enforced by the GPT-5.2 prompt:
 *   ## Frequently Asked Questions
 *   ### Q: question text?
 *   answer paragraph
 *   ### Q: another question?
 *   answer paragraph
 *
 * The function is permissive — it accepts variations like "FAQ", "FAQs", and
 * H3 headings without "Q:" prefix.
 */
export const extractFAQ = (markdown = '') => {
  if (!markdown) return [];

  // Find the FAQ section by heading
  const faqHeadingRegex = /##\s+(Frequently\s+Asked\s+Questions|FAQs?|FAQ)\s*\n/i;
  const m = markdown.match(faqHeadingRegex);
  if (!m) return [];

  const startIdx = m.index + m[0].length;
  // Section ends at the next H2 (## ) or end of string
  const rest = markdown.slice(startIdx);
  const nextH2Idx = rest.search(/\n##\s+/);
  const section = nextH2Idx === -1 ? rest : rest.slice(0, nextH2Idx);

  // Split into Q/A blocks by H3 (### )
  const blocks = section.split(/\n###\s+/).slice(1); // first chunk is preamble
  const items = [];

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length === 0) continue;
    let question = lines[0].trim().replace(/^Q:\s*/i, '').replace(/\s*\?$/, '?').trim();
    if (!question) continue;
    if (!question.endsWith('?')) question = `${question}?`;

    const answerLines = lines
      .slice(1)
      .filter((l) => l.trim() && !l.trim().startsWith('A:'))
      .map((l) => l.replace(/^A:\s*/i, '').trim());
    const answer = answerLines.join(' ').replace(/\s+/g, ' ').trim();
    if (answer.length < 10) continue;

    items.push({ question, answer });
  }

  return items;
};

export default extractFAQ;
